import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { db } from '../lib/firebase';

const MAX = 50;

/**
 * Reportes bajo demanda con rango de fechas.
 * NO descarga todo el histórico. Cada sección usa las colecciones reales del core.
 */
export async function reportSales({ ruc, desde, hasta, pageSize = MAX }) {
  if (!ruc) throw new Error('RUC requerido');
  const constraints = [where('ruc', '==', ruc)];
  if (desde) constraints.push(where('fecha', '>=', desde));
  if (hasta) constraints.push(where('fecha', '<=', hasta));
  constraints.push(orderBy('fecha', 'desc'));
  constraints.push(limit(pageSize));

  const snap = await getDocs(query(collection(db, 'pedidos'), ...constraints));
  const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const total = rows.reduce((s, r) => s + Number(r.total || 0), 0);
  const cancelados = rows.filter((r) => r.cancelado).length;
  const pagados = rows.filter((r) => r.status === 'pagado' && !r.cancelado).length;
  return { rows, total, count: rows.length, pagados, cancelados };
}

export async function reportPurchases({ ruc, desde, hasta, pageSize = MAX }) {
  if (!ruc) throw new Error('RUC requerido');
  const constraints = [where('ruc', '==', ruc)];
  if (desde) constraints.push(where('fechaEmision', '>=', desde));
  if (hasta) constraints.push(where('fechaEmision', '<=', hasta));
  constraints.push(orderBy('fechaEmision', 'desc'));
  constraints.push(limit(pageSize));

  const snap = await getDocs(query(collection(db, 'compras'), ...constraints));
  const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const total = rows.reduce((s, r) => s + Number(r.total || 0), 0);
  return { rows, total, count: rows.length };
}

export async function reportLowStock({ ruc, pageSize = 20 }) {
  if (!ruc) throw new Error('RUC requerido');
  const snap = await getDocs(
    query(collection(db, 'productos'), where('ruc', '==', ruc), where('activo', '==', true), limit(pageSize * 3))
  );
  const rows = snap.docs
    .map((d) => ({ uid: d.id, ...d.data() }))
    .filter((p) => p.controlaStock && Number(p.stock || 0) <= Number(p.stockMinimo || 0))
    .slice(0, pageSize);
  return { rows, count: rows.length };
}

export async function reportInvoices({ ruc, desde, hasta, pageSize = MAX }) {
  if (!ruc) throw new Error('RUC requerido');
  const types = [
    { col: 'factura', label: 'Factura' },
    { col: 'boleta', label: 'Boleta' },
    { col: 'NC', label: 'NC' },
    { col: 'ND', label: 'ND' },
  ];
  const per = Math.max(1, Math.ceil(pageSize / types.length));
  const batches = await Promise.all(
    types.map(async (t) => {
      const constraints = [where('ruc', '==', ruc)];
      if (desde) constraints.push(where('fechaEmision', '>=', desde));
      if (hasta) constraints.push(where('fechaEmision', '<=', hasta));
      constraints.push(orderBy('fechaEmision', 'desc'));
      constraints.push(limit(per));
      const snap = await getDocs(query(collection(db, t.col), ...constraints));
      return snap.docs.map((d) => ({ id: d.id, tipo: t.label, ...d.data() }));
    })
  );
  const rows = batches.flat().sort((a, b) => new Date(b.fechaEmision || 0) - new Date(a.fechaEmision || 0));
  const total = rows.reduce((s, r) => s + Number(r.total || 0), 0);
  return { rows: rows.slice(0, pageSize), total, count: rows.length };
}
